import { conflictCurrent, isRetryable, type AccountClient } from './client';
import { applyDocPatch, bodyOr, isEmptyPatch, mergePatch, type DocPatch } from './documents';
import { intentsOf, type Intents } from './intents';
import { DOC_KEYS, outboxKey, type DocKey } from './keys';
import { isPlainObject, parseJson, safeGet, safeSet, type KeyValueStorage } from './storage';
import type { AccountDocument, AccountState, IntentOp, IntentState } from './types';

/**
 * Account-mode changes the server hasn't confirmed yet, in
 * `mcw.acct.<workosUserId>.outbox`.
 *
 * An entry is written before anything is sent and removed only after the
 * server confirms it, so a change survives a lost connection, a closed tab or
 * a crash, and is sent on the next load. On load the entries are laid over
 * `GET /me/state`, so the screen never shows less than the user's last action.
 *
 * Entries are coalesced: one desired state per event, one patch per document.
 * Each carries the sequence number of its last change, so an entry changed
 * while its request was in flight stays queued.
 *
 * The stored copy is the one every tab works from; this tab's memory stands in
 * only when site data can't be written.
 */

export interface Outbox {
  v: 1;
  seq: number;
  intents: Record<string, { state: IntentState | null; seq: number }>;
  docs: Partial<Record<DocKey, { patch: DocPatch; seq: number }>>;
}

export const emptyOutbox = (): Outbox => ({ v: 1, seq: 0, intents: {}, docs: {} });

export const outboxIsEmpty = (outbox: Outbox): boolean =>
  Object.keys(outbox.intents).length === 0 && Object.keys(outbox.docs).length === 0;

const INTENT_STATES = new Set(['going', 'saved', 'dismissed']);

export function parseOutbox(raw: string | null): Outbox {
  const value = parseJson(raw);
  const out = emptyOutbox();
  if (!isPlainObject(value)) return out;
  out.seq = typeof value.seq === 'number' ? value.seq : 0;
  if (isPlainObject(value.intents)) {
    for (const [id, entry] of Object.entries(value.intents)) {
      if (!isPlainObject(entry) || typeof entry.seq !== 'number') continue;
      if (entry.state !== null && !INTENT_STATES.has(entry.state as string)) continue;
      out.intents[id] = { state: entry.state as IntentState | null, seq: entry.seq };
    }
  }
  if (isPlainObject(value.docs)) {
    for (const key of DOC_KEYS) {
      const entry = value.docs[key];
      if (isPlainObject(entry) && isPlainObject(entry.patch) && typeof entry.seq === 'number') {
        out.docs[key] = { patch: entry.patch as DocPatch, seq: entry.seq };
      }
    }
  }
  return out;
}

export function enqueueIntents(outbox: Outbox, ops: readonly IntentOp[]): Outbox {
  if (ops.length === 0) return outbox;
  let seq = outbox.seq;
  const intents = { ...outbox.intents };
  for (const op of ops) {
    seq += 1;
    intents[op.opportunityId] = { state: op.state, seq };
  }
  return { ...outbox, seq, intents };
}

export function enqueuePatch(outbox: Outbox, key: DocKey, patch: DocPatch): Outbox {
  if (isEmptyPatch(patch)) return outbox;
  const seq = outbox.seq + 1;
  const older = outbox.docs[key]?.patch ?? {};
  return { ...outbox, seq, docs: { ...outbox.docs, [key]: { patch: mergePatch(older, patch), seq } } };
}

/** Drops the entries the server confirmed, unless they changed while in flight. */
export function settleIntents(outbox: Outbox, sent: Record<string, number>): Outbox {
  const intents = { ...outbox.intents };
  for (const [id, seq] of Object.entries(sent)) if (intents[id]?.seq === seq) delete intents[id];
  return { ...outbox, intents };
}

export function settleDoc(outbox: Outbox, key: DocKey, sentSeq: number): Outbox {
  if (outbox.docs[key]?.seq !== sentSeq) return outbox;
  const docs = { ...outbox.docs };
  delete docs[key];
  return { ...outbox, docs };
}

/** What the screen shows: the account as last read, with every queued change on top. */
export interface Display {
  intents: Intents;
  docs: Record<DocKey, unknown>;
}

export function applyOutbox(state: AccountState, outbox: Outbox): Display {
  const intents = intentsOf(state);
  for (const [id, { state: next }] of Object.entries(outbox.intents)) {
    if (next === null) intents.delete(id);
    else if (intents.get(id) !== next) {
      intents.delete(id);
      intents.set(id, next);
    }
  }
  const docs = {} as Record<DocKey, unknown>;
  for (const key of DOC_KEYS) {
    const base = bodyOr(key, state.documents[key]?.body);
    const pending = outbox.docs[key];
    docs[key] = pending ? applyDocPatch(key, base, pending.patch) : base;
  }
  return { intents, docs };
}

// --- persistence ---------------------------------------------------------------

export class OutboxStore {
  private memory: Outbox;
  /** False once a write failed: memory is then ahead of the stored copy. */
  private stored = true;
  readonly key: string;
  private readonly storage: KeyValueStorage;

  constructor(storage: KeyValueStorage, workosUserId: string) {
    this.storage = storage;
    this.key = outboxKey(workosUserId);
    this.memory = parseOutbox(safeGet(storage, this.key));
  }

  read(): Outbox {
    if (this.stored) this.memory = parseOutbox(safeGet(this.storage, this.key));
    return this.memory;
  }

  /** Read, change and write back in one synchronous step. */
  update(change: (outbox: Outbox) => Outbox): Outbox {
    const next = change(this.read());
    this.memory = next;
    this.stored = safeSet(this.storage, this.key, outboxIsEmpty(next) ? null : JSON.stringify(next));
    return next;
  }
}

// --- sending -------------------------------------------------------------------

/** The batch endpoint's limit. */
const MAX_OPS = 500;
/** Conflicts in a row before a document waits for the next flush. */
const MAX_CONFLICTS = 4;

export interface FlushResult {
  /** Nothing is left queued. */
  done: boolean;
  /** Something failed that may work later (offline, 5xx, repeated conflicts). */
  retry: boolean;
  /** Refused by the server (4xx): left queued, and reported. */
  refused: { status: number; code: string | null; what: 'intents' | DocKey }[];
  /** Intent ops the server answered `ok: false` for, dropped: they can never apply. */
  dropped: number;
}

/**
 * Sends every queued change, oldest document first, and settles what the
 * server confirms. `docs` is the server's copy of each document as last known,
 * updated in place as versions move.
 */
export async function flushOutbox(
  store: OutboxStore,
  client: Pick<AccountClient, 'batchIntents' | 'putDocument'>,
  docs: Record<DocKey, AccountDocument | null>,
): Promise<FlushResult> {
  const result: FlushResult = { done: false, retry: false, refused: [], dropped: 0 };

  // Intents, in chunks the endpoint accepts.
  const queued = Object.entries(store.read().intents);
  for (let at = 0; at < queued.length; at += MAX_OPS) {
    const chunk = queued.slice(at, at + MAX_OPS);
    const sent: Record<string, number> = {};
    for (const [id, entry] of chunk) sent[id] = entry.seq;
    try {
      const answer = await client.batchIntents(chunk.map(([id, entry]) => ({ opportunityId: id, state: entry.state })));
      result.dropped += answer.results.filter((r) => !r.ok).length;
      store.update((outbox) => settleIntents(outbox, sent));
    } catch (err) {
      if (isRetryable(err)) result.retry = true;
      else result.refused.push({ ...statusOf(err), what: 'intents' });
      break;
    }
  }

  // Documents, one PUT each, merged again on a conflict.
  for (const key of DOC_KEYS) {
    for (let conflicts = 0; ; conflicts += 1) {
      const entry = store.read().docs[key];
      if (!entry) break;
      if (conflicts >= MAX_CONFLICTS) {
        result.retry = true;
        break;
      }
      const base = docs[key];
      const body = applyDocPatch(key, base?.body, entry.patch);
      try {
        const { version } = await client.putDocument(key, body, base?.version ?? 0);
        docs[key] = { body, version };
        store.update((outbox) => settleDoc(outbox, key, entry.seq));
        break;
      } catch (err) {
        const current = conflictCurrent(err);
        if (current) {
          // Someone else saved first: take their copy and lay this patch over it again.
          docs[key] = current;
          continue;
        }
        if (isRetryable(err)) result.retry = true;
        else result.refused.push({ ...statusOf(err), what: key });
        break;
      }
    }
  }

  result.done = outboxIsEmpty(store.read());
  return result;
}

function statusOf(err: unknown): { status: number; code: string | null } {
  const e = err as { status?: unknown; code?: unknown } | null;
  return {
    status: typeof e?.status === 'number' ? e.status : 0,
    code: typeof e?.code === 'string' ? e.code : null,
  };
}
